import { pdfjsWorkerUrl } from "@/utils/pdfWorker";
import { isPdfFile } from "@/utils/storageUtils";
import { Mutex } from "async-mutex";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

type OpenOutcome = "opened" | "needs-password" | "parse-failed" | "read-failed";

const isEmptyPasswordRejection = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === "PasswordException" && code === pdfjsLib.PasswordResponses.NEED_PASSWORD;
};

const logCheckFailure = (phase: string, fileSize: number, error: unknown): void => {
  const name = error instanceof Error ? error.name : typeof error;
  // No filename: it's a patient document name and may be PHI.
  console.error(`[pdfEncryptionCheck] ${phase} failed, size=${fileSize}: ${name}`);
};

/** Thrown when a PDF could not be opened at all, so password-protection status is unknown. */
export class PdfCheckFailedError extends Error {
  constructor() {
    super("Could not open this PDF to check whether it is password-protected.");
    this.name = "PdfCheckFailedError";
  }
}

/** Feeds pdf.js range requests from File slices; readFailed surfaces read errors pdf.js has no channel for. */
class FileRangeTransport extends pdfjsLib.PDFDataRangeTransport {
  readonly readFailed: Promise<never>;
  private rejectRead: (error: unknown) => void = () => {};
  private aborted = false;

  constructor(private readonly file: File) {
    super(file.size, null);
    this.readFailed = new Promise<never>((_, reject) => {
      this.rejectRead = reject;
    });
  }

  override requestDataRange(begin: number, end: number): void {
    this.file
      .slice(begin, end)
      .arrayBuffer()
      .then((buffer) => {
        // pdf.js asserts on chunks that arrive after its range readers were cancelled.
        if (!this.aborted) this.onDataRange(begin, new Uint8Array(buffer));
      })
      .catch(this.rejectRead);
  }

  override abort(): void {
    this.aborted = true;
  }
}

type RaceResult =
  | { source: "parse"; ok: true }
  | { source: "parse"; ok: false; error: unknown }
  | { source: "read"; ok: false; error: unknown };

const openByRanges = async (file: File): Promise<OpenOutcome> => {
  const transport = new FileRangeTransport(file);
  const loadingTask = pdfjsLib.getDocument({
    range: transport,
    disableAutoFetch: true,
    disableStream: true,
  });

  // Tags which side rejected instead of merging both into one error, so a
  // genuine parse failure (full read won't fix it either) can be told apart
  // from a transport read failure (worth retrying with a full read).
  const settled = await Promise.race<RaceResult>([
    loadingTask.promise
      .then((): RaceResult => ({ source: "parse", ok: true }))
      .catch((error: unknown): RaceResult => ({ source: "parse", ok: false, error })),
    transport.readFailed.catch(
      (error: unknown): RaceResult => ({ source: "read", ok: false, error })
    ),
  ]);
  await loadingTask.destroy();

  if (settled.ok) return "opened";
  if (settled.source === "read") {
    logCheckFailure("range-read", file.size, settled.error);
    return "read-failed";
  }
  if (isEmptyPasswordRejection(settled.error)) return "needs-password";
  logCheckFailure("range-parse", file.size, settled.error);
  return "parse-failed";
};

// Guards the only path that buffers a whole file in memory, so at most one
// full-file fallback runs at a time no matter how many files are being
// checked concurrently.
const fullFileLoadMutex = new Mutex();

const openFullFile = (file: File): Promise<OpenOutcome> =>
  fullFileLoadMutex.runExclusive(async () => {
    const data = await file.arrayBuffer().catch((error: unknown) => {
      logCheckFailure("full-read", file.size, error);
      return null;
    });
    if (!data) return "parse-failed";

    const loadingTask = pdfjsLib.getDocument({ data });
    try {
      await loadingTask.promise;
      return "opened";
    } catch (error) {
      if (isEmptyPasswordRejection(error)) return "needs-password";
      logCheckFailure("full-parse", file.size, error);
      return "parse-failed";
    } finally {
      await loadingTask.destroy();
    }
  });

/**
 * Detects PDFs requiring a real password to open (empty password rejected).
 * - Range-reads the trailer/xref/Encrypt dict only. Retries with a full,
 *   mutex-guarded read only when the range transport itself failed to read —
 *   a genuine parse failure would fail the same way on a full read, so it
 *   isn't retried.
 * - Permissions-only PDFs (empty user password) are not flagged — matches pdf.js's own behavior.
 * - Throws PdfCheckFailedError when the PDF could not be opened at all (corrupt or otherwise
 *   unreadable): that's a distinct outcome from "confirmed not password-protected" and callers
 *   must decide how to handle it explicitly, rather than it silently resolving to false.
 */
export const isPasswordProtectedPdf = async (file: File): Promise<boolean> => {
  if (!isPdfFile(file)) return false;

  const rangeOutcome = await openByRanges(file);

  if (rangeOutcome === "parse-failed") throw new PdfCheckFailedError();

  if (rangeOutcome === "needs-password") return true;
  if (rangeOutcome === "opened") return false;

  // rangeOutcome === "read-failed": the transport couldn't read a byte range,
  // not a parse problem — retry once with a full, mutex-guarded read.
  const fullFileOutcome = await openFullFile(file);

  if (fullFileOutcome === "needs-password") return true;
  if (fullFileOutcome === "opened") return false;

  throw new PdfCheckFailedError();
};
